import React, { useState, useEffect } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { BOOKMAKER_ALIASES } from '../utils/constants';
import { useBooks } from '../contexts/BooksContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const bookmakerColors = {
  fanduel: '#1A3D6C',
  draftkings: '#5DD335',
  betmgm: '#FFCC00',
  fanatics: '#002F6C',
  espnbet: '#FF0033',
  betrivers: '#EC1C24',
  ballybet: '#000000',
  bovada: '#CC0000',
  caesars: '#000000',
  hardrockbet: '#D4145A',
  bet365: '#027B5B'
};

const getMarketAbbreviation = (marketKey) => {
  if (!marketKey) return '';
  const marketMap = {
    'batter_hits': 'H', 'batter_rbis': 'RBI', 'batter_runs_scored': 'R',
    'batter_hits_runs_rbis': 'H+R+R', 'batter_home_runs': 'HR', 'batter_doubles': '2B',
    'batter_triples': '3B', 'batter_total_bases': 'TB', 'batter_stolen_bases': 'SB',
    'pitcher_strikeouts': 'K', 'pitcher_hits_allowed': 'Hits', 'pitcher_outs': 'Outs',
    'player_points': 'PTS', 'player_rebounds': 'REB', 'player_assists': 'AST',
    'player_threes': '3PM', 'player_points_rebounds_assists': 'PRA',
    'player_points_rebounds': 'P+R', 'player_points_assists': 'P+A',
    'player_rebounds_assists': 'R+A', 'player_1st_td': '1st TD',
    'player_tds_over': 'TDs', 'player_pass_yds': 'PaYds', 'player_rush_yds': 'RuYds',
    'player_receptions': 'REC', 'player_reception_yds': 'ReYds',
    'player_rush_reception_yds': 'Ru+RecYds', 'player_field_goals': 'FG'
  };
  return marketMap[marketKey] || marketKey.replace('player_', '').replace(/_/g, ' ').toUpperCase();
};

const PlayerOddsChart = ({ gameId, playerId, marketKey, point, isOver, sport, onBack }) => {
  const { isBookEnabledForGraphs } = useBooks();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gameId || !playerId || !marketKey) return;

    const fetchAndPrepare = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/games/${sport}/${gameId}/${playerId}/${marketKey}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const prepared = prepareChartData(data, point, isOver);
        setChartData(prepared);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAndPrepare();
  }, [gameId, playerId, marketKey, point, isOver, sport]);

  const prepareChartData = (dataSource, selectedTier, useOver) => {
    if (!dataSource || !dataSource.oddsHistory || dataSource.oddsHistory.length === 0) return null;

    const filteredHistory = dataSource.oddsHistory.filter(entry => {
      if (marketKey && entry.marketType !== marketKey) return false;
      if (selectedTier !== null && selectedTier !== undefined) {
        return entry.point === selectedTier;
      }
      return true;
    });

    if (filteredHistory.length === 0) return null;

    const allBookmakers = new Set();
    filteredHistory.forEach(entry => {
      if (entry.books && typeof entry.books === 'object') {
        Object.keys(entry.books).forEach(book => {
          if (entry.books[book]) {
            if (entry.books[book].over !== undefined ||
                entry.books[book].yes !== undefined ||
                typeof entry.books[book] === 'number') {
              allBookmakers.add(book);
            }
          }
        });
      }
    });

    // Include every book in the chart so users can toggle them on/off via the
    // legend. The Settings "Graphs" toggle only controls each dataset's INITIAL
    // hidden state (handled below where the dataset object is built).
    const bookmakers = Array.from(allBookmakers);
    if (bookmakers.length === 0) return null;

    const groupedByTimestamp = {};
    filteredHistory.forEach(entry => {
      if (!entry.timestamp) return;
      const date = new Date(entry.timestamp);
      const minutes = date.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;
      date.setMinutes(roundedMinutes);
      date.setSeconds(0);
      date.setMilliseconds(0);
      const normalizedTimestamp = date.toISOString();
      if (!groupedByTimestamp[normalizedTimestamp]) {
        groupedByTimestamp[normalizedTimestamp] = [];
      }
      groupedByTimestamp[normalizedTimestamp].push(entry);
    });

    const timestamps = Object.keys(groupedByTimestamp).sort((a, b) => new Date(a) - new Date(b));
    if (timestamps.length === 0) return null;

    const labels = timestamps.map(timestamp => {
      try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      } catch (e) {
        return 'Invalid Date';
      }
    });

    const datasetsWithLatestValues = bookmakers
      .map(bookmaker => {
        const data = timestamps.map(timestamp => {
          const entries = groupedByTimestamp[timestamp];
          const values = [];
          entries.forEach(entry => {
            if (entry.books && entry.books[bookmaker]) {
              let value;
              if (selectedTier === null || selectedTier === undefined) {
                if (entry.books[bookmaker].yes !== undefined) {
                  value = parseFloat(entry.books[bookmaker].yes);
                } else if (entry.books[bookmaker].over !== undefined) {
                  value = parseFloat(entry.books[bookmaker].over);
                } else if (typeof entry.books[bookmaker] === 'number') {
                  value = parseFloat(entry.books[bookmaker]);
                }
              } else {
                if (useOver) {
                  if (entry.books[bookmaker].over !== undefined) {
                    value = parseFloat(entry.books[bookmaker].over);
                  } else if (typeof entry.books[bookmaker] === 'number') {
                    value = parseFloat(entry.books[bookmaker]);
                  }
                } else {
                  if (entry.books[bookmaker].under !== undefined) {
                    value = parseFloat(entry.books[bookmaker].under);
                  }
                }
              }
              if (value !== undefined && !isNaN(value)) values.push(value);
            }
          });
          if (values.length > 0) return Math.max(...values);
          return null;
        });

        const hasData = data.some(value => value !== null);
        if (!hasData) return null;

        let latestValue = null;
        for (let i = data.length - 1; i >= 0; i--) {
          if (data[i] !== null) { latestValue = data[i]; break; }
        }

        const pointRadius = data.map((value, index) => {
          if (value === null) return 0;
          if (index === 0 || index === data.length - 1) return 3;
          let prevValue = null;
          for (let i = index - 1; i >= 0; i--) {
            if (data[i] !== null) { prevValue = data[i]; break; }
          }
          return (prevValue !== null && value !== prevValue) ? 3 : 0;
        });

        // Resolve alias so Settings toggles / color map / display label all
        // key on the same canonical name (e.g. `williamhill_us` → `caesars`).
        const canonical = BOOKMAKER_ALIASES[bookmaker] || bookmaker;

        return {
          label: canonical.charAt(0).toUpperCase() + canonical.slice(1),
          data,
          borderColor: bookmakerColors[canonical] || '#999999',
          backgroundColor: bookmakerColors[canonical] || '#999999',
          tension: 0.1,
          pointRadius,
          pointHoverRadius: 5,
          spanGaps: true,
          borderWidth: 1.5,
          // Initial hidden state driven by Settings → Graphs toggle. The user
          // can click the legend to flip it on without changing Settings.
          hidden: isBookEnabledForGraphs ? !isBookEnabledForGraphs(canonical) : false,
          latestValue
        };
      })
      .filter(dataset => dataset !== null);

    const datasets = datasetsWithLatestValues
      .sort((a, b) => (b.latestValue || -Infinity) - (a.latestValue || -Infinity))
      .map(({ latestValue, ...rest }) => rest);

    if (datasets.length === 0 || labels.length === 0) return null;

    return { labels, datasets };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 8, font: { size: 9 }, color: '#9CA3AF', padding: 6 }
      },
      title: {
        display: true,
        text: `${getMarketAbbreviation(marketKey)}${point !== null && point !== undefined ? ` ${isOver ? 'Over' : 'Under'} ${point}` : ''}`,
        font: { size: 12 },
        color: '#e5e7eb'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            if (value === null) return null;
            return `${context.dataset.label}: ${value > 0 ? '+' : ''}${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { font: { size: 8 }, color: '#6b7280', maxRotation: 45, autoSkip: true, maxTicksLimit: 8 },
        grid: { color: 'rgba(75,85,99,0.3)' }
      },
      y: {
        ticks: {
          font: { size: 9 },
          color: '#6b7280',
          callback: function(value) { return value > 0 ? `+${value}` : value; }
        },
        grid: { color: 'rgba(75,85,99,0.3)' }
      }
    },
    interaction: { intersect: false, mode: 'index' }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
        <Spinner animation="border" size="sm" variant="light" />
      </div>
    );
  }

  if (error) {
    return <div style={{ color: '#ef4444', fontSize: '0.75rem', padding: '8px' }}>Error: {error}</div>;
  }

  if (!chartData) {
    return <div style={{ color: '#9CA3AF', fontSize: '0.75rem', padding: '8px' }}>No chart data available</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      <button
        onClick={(e) => { e.stopPropagation(); onBack(); }}
        style={{
          position: 'absolute',
          top: '2px',
          right: '2px',
          background: 'transparent',
          border: 'none',
          color: '#9CA3AF',
          fontSize: '18px',
          cursor: 'pointer',
          zIndex: 1,
          lineHeight: 1,
          padding: '2px 6px'
        }}
      >
        ×
      </button>
      <div style={{ flex: 1, minHeight: 0 }}>
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default PlayerOddsChart;
